import XHRAdapter from '../utils/xhr';
import { ApiConfig, ApiResponse, XHRResponse } from '../../types/api';

export abstract class BaseApi {
  protected config: ApiConfig

  constructor(config: ApiConfig = {}) {
    this.config = {
      ...config
    };
  }

  public updateConfig(config: ApiConfig = {}) {
    this.config = {
      ...this.config,
      ...config
    };
  }

  private getFullUrl(url: string): string {
    if (/^(http|https):\/\//.test(url)) {
      return url;
    } else {
      return this.config.baseURL + url;
    }
  }

  protected async get<T>(url: string, options: {
    headers?: Record<string, string>;
    showLoading?: boolean;
    noHeader?: boolean;
  } = {}): Promise<ApiResponse<T>> {
    const { headers, showLoading = true, noHeader = false } = options;

    return XHRAdapter.get<ApiResponse<T>>(this.getFullUrl(url), {
      headers: {
        ...this.config.headers,
        ...headers
      },
      showLoading,
      noHeader,
    }).then((response: XHRResponse<ApiResponse<T>>) => {
      return response.data;
    });
  }

  protected async post<T>(url: string, data?: any, options: {
    headers?: Record<string, string>;
    showLoading?: boolean;
  } = {}): Promise<ApiResponse<T>> {
    const { headers, showLoading = true } = options;
    return XHRAdapter.post<ApiResponse<T>>(this.getFullUrl(url), {
      body: data,
      headers: {
        ...this.config.headers,
        ...headers
      },
      showLoading
    }).then((response: XHRResponse<ApiResponse<T>>) => {
      return response.data;
    });
  }

  protected async request<T>(options: {
    method: string;
    url: string;
    data?: any;
    headers?: Record<string, string>;
    showLoading?: boolean;
  }): Promise<ApiResponse<T>> {
    const { method, url, data, headers, showLoading = true } = options;
    
    return XHRAdapter.request({
      method,
      url: this.getFullUrl(url),
      body: data,
      headers: {
        ...this.config.headers,
        ...headers
      },
      showLoading
    }).then((response: XHRResponse<ApiResponse<T>>) => {
      return response.data;
    });
  }

  protected async requestWithRetry<T>(options: {
    method: string;
    url: string;
    data?: any;
    headers?: Record<string, string>;
    showLoading?: boolean;
  }, retryCount: number = 3): Promise<ApiResponse<T>> {
    let lastResponse: ApiResponse<T> | null = null;
    let attempts = 0;

    while (attempts < retryCount) {
      try {
        const response = await this.request<T>(options);
        if (response.code === 200) {
          return response;
        }
        lastResponse = response;
        attempts++;
        if (attempts < retryCount) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
        }
      } catch (error) {
        if (attempts === retryCount - 1) {
          throw error;
        }
        attempts++;
        // 等待后继续重试
        await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
      }
    }

    return lastResponse!;
  }
}